import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { User, Lock, Building, Crown, Calendar, ArrowRight, RotateCcw } from "lucide-react";
import { format } from "date-fns";
import ReferralWidget from "@/components/ReferralWidget";

export default function SettingsPage() {
  const { user, refreshUser } = useAuth();

  return (
    <ScrollArea className="h-full">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-8 page-enter" data-testid="settings-page">
        <div>
          <h2 className="font-heading text-xl font-medium text-[#111111]" data-testid="settings-title">Settings</h2>
          <p className="text-sm text-[#888888] mt-1">Manage your account, subscription, and organization.</p>
        </div>

        {/* Subscription Section */}
        <SubscriptionSection />

        <Separator />

        {/* Referral Section */}
        <ReferralWidget />

        <Separator />

        {/* Profile Section */}
        <ProfileSection user={user} onUpdate={refreshUser} />

        <Separator />

        {/* Change Password */}
        <PasswordSection user={user} />

        <Separator />

        {/* Tenant Info */}
        <TenantSection user={user} />
      </div>
    </ScrollArea>
  );
}

function SubscriptionSection() {
  const [planStatus, setPlanStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchStatus = async () => {
    try {
      const { data } = await api.get("/payments/plan-status");
      setPlanStatus(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleCancel = async () => {
    if (!window.confirm("Are you sure you want to cancel your subscription? You'll keep Pro access until the end of the billing period.")) return;
    setActionLoading(true);
    try {
      await api.post("/payments/cancel");
      toast.success("Subscription canceled");
      fetchStatus();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    setActionLoading(true);
    try {
      await api.post("/payments/reactivate");
      toast.success("Subscription reactivated");
      fetchStatus();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return null;

  const isPro = planStatus?.is_pro;
  const subStatus = planStatus?.subscription_status;
  const periodEnd = planStatus?.current_period_end ? new Date(planStatus.current_period_end) : null;
  const intervalLabel = planStatus?.subscription_interval === "yearly" ? "Yearly" : "Monthly";

  return (
    <div className="space-y-4" data-testid="subscription-section">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
          <Crown size={18} className="text-blue-600" />
        </div>
        <div>
          <h3 className="font-heading text-lg font-medium text-[#111111]">Subscription</h3>
          <p className="text-xs text-zinc-500">Manage your plan and billing</p>
        </div>
      </div>

      <div className="pl-[52px]">
        {isPro ? (
          <div className="space-y-3">
            <div className="p-4 rounded-lg border border-[#E5E5E5] bg-white space-y-3">
              <div className="flex items-center gap-2">
                <Badge className="bg-blue-600 text-white text-[10px] font-mono" data-testid="sub-plan-badge">PRO</Badge>
                <Badge variant="outline" className="text-[10px] font-mono" data-testid="sub-interval-badge">
                  <Calendar size={10} className="mr-1" /> {intervalLabel}
                </Badge>
                {subStatus === "canceled" && (
                  <Badge className="bg-amber-100 text-amber-600 text-[10px] font-mono border-0" data-testid="sub-canceled-badge">CANCELS AT PERIOD END</Badge>
                )}
                {subStatus === "active" && (
                  <Badge className="bg-green-100 text-green-600 text-[10px] font-mono border-0" data-testid="sub-active-badge">ACTIVE</Badge>
                )}
              </div>
              {periodEnd && (
                <p className="text-xs text-zinc-500" data-testid="sub-period-end">
                  {subStatus === "canceled"
                    ? `Pro access until ${format(periodEnd, "MMM d, yyyy")}`
                    : `Next renewal: ${format(periodEnd, "MMM d, yyyy")}`}
                </p>
              )}
            </div>

            {subStatus === "active" && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                disabled={actionLoading}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="sub-cancel-button"
              >
                {actionLoading ? "Processing..." : "Cancel subscription"}
              </Button>
            )}

            {subStatus === "canceled" && (
              <Button
                size="sm"
                onClick={handleReactivate}
                disabled={actionLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="sub-reactivate-button"
              >
                {actionLoading ? "Processing..." : <><RotateCcw size={13} className="mr-1.5" /> Reactivate subscription</>}
              </Button>
            )}
          </div>
        ) : (
          <div className="p-4 rounded-lg border border-[#E5E5E5] bg-[#F5F5F5] space-y-3">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px] font-mono text-zinc-500" data-testid="sub-free-badge">FREE PLAN</Badge>
            </div>
            <p className="text-sm text-[#888888]">Upgrade to Pro for unlimited aliases, categories, and more.</p>
            <Link to="/pricing">
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="sub-upgrade-button">
                View plans <ArrowRight size={13} className="ml-1.5" />
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function ProfileSection({ user, onUpdate }) {
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/auth/me", { name });
      toast.success("Profile updated");
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="profile-section">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#F5F5F5] flex items-center justify-center">
          <User size={18} className="text-[#888888]" />
        </div>
        <div>
          <h3 className="font-heading text-lg font-medium text-[#111111]">Profile</h3>
          <p className="text-xs text-zinc-500">Your personal information</p>
        </div>
      </div>
      <div className="space-y-3 pl-[52px]">
        <div className="space-y-2">
          <Label className="text-sm text-[#555555]">Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="max-w-sm h-10" data-testid="settings-name-input" />
        </div>
        <div className="space-y-2">
          <Label className="text-sm text-[#555555]">Email</Label>
          <Input value={user?.email || ""} disabled className="max-w-sm h-10 bg-[#F5F5F5] font-mono text-sm" data-testid="settings-email-display" />
        </div>
        <Button onClick={handleSave} disabled={saving} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="settings-save-profile">
          {saving ? "Saving..." : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

function PasswordSection({ user }) {
  const [current, setCurrent] = useState("");
  const [newPw, setNewPw] = useState("");
  const [saving, setSaving] = useState(false);

  // Google-only users don't have a password
  const isGoogleOnly = user?.auth_provider === "google";

  const handleChange = async () => {
    if (newPw.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: newPw });
      toast.success("Password changed");
      setCurrent("");
      setNewPw("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (isGoogleOnly) {
    return (
      <div className="space-y-4" data-testid="password-section">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#F5F5F5] flex items-center justify-center">
            <Lock size={18} className="text-[#888888]" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-medium text-[#111111]">Security</h3>
            <p className="text-xs text-zinc-500">Signed in with Google</p>
          </div>
        </div>
        <p className="text-sm text-[#888888] pl-[52px]">Your account uses Google authentication. No password to manage.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="password-section">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#F5F5F5] flex items-center justify-center">
          <Lock size={18} className="text-[#888888]" />
        </div>
        <div>
          <h3 className="font-heading text-lg font-medium text-[#111111]">Security</h3>
          <p className="text-xs text-zinc-500">Change your password</p>
        </div>
      </div>
      <div className="space-y-3 pl-[52px]">
        <div className="space-y-2">
          <Label className="text-sm text-[#555555]">Current Password</Label>
          <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="max-w-sm h-10" data-testid="settings-current-password" />
        </div>
        <div className="space-y-2">
          <Label className="text-sm text-[#555555]">New Password</Label>
          <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Min. 8 characters" className="max-w-sm h-10" data-testid="settings-new-password" />
        </div>
        <Button onClick={handleChange} disabled={saving || !current || !newPw} size="sm" variant="outline" data-testid="settings-change-password">
          {saving ? "Changing..." : "Change password"}
        </Button>
      </div>
    </div>
  );
}

function TenantSection({ user }) {
  return (
    <div className="space-y-4" data-testid="tenant-section">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#F5F5F5] flex items-center justify-center">
          <Building size={18} className="text-[#888888]" />
        </div>
        <div>
          <h3 className="font-heading text-lg font-medium text-[#111111]">Organization</h3>
          <p className="text-xs text-zinc-500">Your tenant information</p>
        </div>
      </div>
      <div className="space-y-3 pl-[52px]">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="p-3 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5]">
            <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Organization</p>
            <p className="text-sm font-medium text-[#111111] mt-1">{user?.tenant?.name || "N/A"}</p>
          </div>
          <div className="p-3 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5]">
            <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Role</p>
            <p className="text-sm font-medium text-[#111111] mt-1 capitalize">{user?.role || "user"}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
